import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';

class TrackingService {
  constructor() {
    this.visitorId = this.getOrCreateVisitorId();
    this.sessionId = this.createSessionId();
    this.sessionStartTime = Date.now();
    this.API_ENDPOINT = process.env.REACT_APP_TRACKING_API_ENDPOINT;
    
    // Add these new properties for heat map tracking
    this.clickData = [];
    this.moveData = [];
    this.scrollData = [];
    this.viewportWidth = window.innerWidth;
    this.viewportHeight = window.innerHeight;
    this.moveCounter = 0;
    this.moveSamplingRate = 5; // Only track 1 in 5 mousemove events
    
    this.initializeSession();
    this.initializeHeatMapTracking();

    // Use proper base URL for development and production
    this.baseUrl = process.env.NODE_ENV === 'development' 
      ? 'http://localhost:3000/api'  // Development
      : 'https://api.causeandimpact.org/api'; // Production
    
    this.endpoints = {
      track: `${this.baseUrl}/analytics/track`,
      heatmap: `${this.baseUrl}/analytics/heatmap`
    };
    
    this.isEnabled = process.env.REACT_APP_ENABLE_ANALYTICS === 'true';
    this.debug = process.env.NODE_ENV === 'development';
  }

  // Add logging methods
  log(...args) {
    if (this.debug) {
      console.log('[TrackingService]', ...args);
    }
  }

  logError(...args) {
    if (this.debug) {
      console.error('[TrackingService]', ...args);
    }
  }

  initializeSession() {
    this.log('Initializing tracking session...');
    this.trackEvent('session_start', {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: navigator.language,
      platform: navigator.platform,
      cookiesEnabled: navigator.cookieEnabled,
      deviceMemory: navigator.deviceMemory,
      connectionType: navigator.connection?.effectiveType,
      deviceType: this.getDeviceType(),
      browserFeatures: this.detectBrowserFeatures()
    });
  }

  detectBrowserFeatures() {
    return {
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      webGL: !!window.WebGLRenderingContext,
      touch: 'ontouchstart' in window,
      push: 'PushManager' in window,
      geolocation: 'geolocation' in navigator,
      bluetooth: 'bluetooth' in navigator,
      onlineStatus: navigator.onLine
    };
  }

  getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated/i.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  // Visitor identification
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('visitor_id');
    if (!visitorId) {
      visitorId = uuidv4();
      localStorage.setItem('visitor_id', visitorId);
    }
    return visitorId;
  }

  createSessionId() {
    return uuidv4();
  }

  // Core tracking method
  async trackEvent(eventType, data = {}) {
    if (!this.isEnabled) {
      this.log('Analytics tracking disabled, skipping event:', eventType);
      return Promise.resolve();
    }

    const eventData = {
      visitorId: this.visitorId,
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      eventType,
      page: window.location.pathname,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      ...data
    };

    this.log(`Tracking event: ${eventType}`, eventData);

    return axios.post(this.endpoints.track, eventData, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NODE_ENV === 'development' ? {
          'Access-Control-Allow-Origin': '*'
        } : {})
      }
    })
    .catch(error => {
      this.logError(`Failed to track event ${eventType}:`, error);
      return null; // Don't throw errors for analytics failures
    });
  }

  // Specific tracking methods
  trackPageView() {
    return this.trackEvent('page_view');
  }

  trackClick(element, data = {}) {
    return this.trackEvent('click', {
      elementId: element.id,
      elementClass: element.className,
      elementText: element.innerText,
      ...data
    });
  }

  trackDownload(fileInfo) {
    return this.trackEvent('download', fileInfo);
  }

  trackScroll(depth) {
    return this.trackEvent('scroll_depth', { depth });
  }

  trackFormInteraction(formId, action, data = {}) {
    return this.trackEvent('form_interaction', {
      formId,
      action,
      ...data
    });
  }

  trackSearch(query, results) {
    return this.trackEvent('search', {
      query,
      resultCount: results.length
    });
  }

  // Enhanced tracking methods
  trackInteractionTiming(interactionType, duration) {
    return this.trackEvent('interaction_timing', {
      type: interactionType,
      duration,
      timestamp: Date.now()
    });
  }

  trackEngagement(data) {
    return this.trackEvent('engagement', {
      timeOnPage: Date.now() - this.sessionStartTime,
      scrollDepth: this.calculateScrollDepth(),
      interactionCount: this.getInteractionCount(),
      ...data
    });
  }

  trackError(error, componentStack) {
    return this.trackEvent('error', {
      message: error.message,
      stack: error.stack,
      componentStack,
      timestamp: Date.now()
    });
  }

  trackResourceTiming(resource) {
    const timing = performance.getEntriesByName(resource)[0];
    return this.trackEvent('resource_timing', {
      resource,
      timing: {
        duration: timing.duration,
        startTime: timing.startTime,
        fetchStart: timing.fetchStart,
        responseEnd: timing.responseEnd
      }
    });
  }

  // Add this method to initialize heat map tracking
  initializeHeatMapTracking() {
    // Track clicks
    document.addEventListener('click', (e) => {
      const x = e.pageX;
      const y = e.pageY;
      const elementType = e.target.tagName;
      const elementId = e.target.id;
      const elementClass = e.target.className;
      const elementText = e.target.innerText;
      
      this.clickData.push({
        x, y, 
        relativeX: x / this.viewportWidth,
        relativeY: y / this.viewportHeight,
        elementType,
        elementId,
        elementClass,
        elementText,
        timestamp: Date.now()
      });
      
      // Send click data if we have enough
      if (this.clickData.length >= 10) {
        this.sendHeatMapData('click');
      }
    });
    
    // Track mouse movements (sampled)
    document.addEventListener('mousemove', (e) => {
      this.moveCounter++;
      if (this.moveCounter % this.moveSamplingRate !== 0) return;
      
      const x = e.pageX;
      const y = e.pageY;
      
      this.moveData.push({
        x, y,
        relativeX: x / this.viewportWidth,
        relativeY: y / this.viewportHeight,
        timestamp: Date.now()
      });
      
      // Send movement data if we have enough
      if (this.moveData.length >= 50) {
        this.sendHeatMapData('move');
      }
    });
    
    // Track scroll positions
    let lastScrollY = window.scrollY;
    let scrollTimeout;
    
    document.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      
      scrollTimeout = setTimeout(() => {
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;
        const pageHeight = document.documentElement.scrollHeight;
        
        // Only record if scroll position changed significantly
        if (Math.abs(scrollY - lastScrollY) > viewportHeight * 0.1) {
          this.scrollData.push({
            y: scrollY,
            relativeY: scrollY / pageHeight,
            viewportHeight,
            pageHeight,
            timestamp: Date.now()
          });
          
          lastScrollY = scrollY;
          
          // Send scroll data if we have enough
          if (this.scrollData.length >= 5) {
            this.sendHeatMapData('scroll');
          }
        }
      }, 200);
    });
    
    // Send data before page unload
    window.addEventListener('beforeunload', () => {
      this.sendHeatMapData('all');
    });
    
    // Send data periodically
    setInterval(() => {
      this.sendHeatMapData('all');
    }, 30000); // Every 30 seconds

    // Track all page navigations, including SPA route changes
    this.trackAllPageNavigations();
    
    // Track dynamic content areas
    this.trackDynamicContentInteractions();
    
    // Track portal-specific interactions
    this.trackPortalInteractions();
    
    // Track admin dashboard interactions
    this.trackAdminInteractions();
  }

  // Add this method to send heat map data
  sendHeatMapData(type) {
    let dataToSend = {};
    
    if (type === 'click' || type === 'all') {
      if (this.clickData.length > 0) {
        dataToSend.clicks = [...this.clickData];
        this.clickData = [];
      }
    }
    
    if (type === 'move' || type === 'all') {
      if (this.moveData.length > 0) {
        dataToSend.movements = [...this.moveData];
        this.moveData = [];
      }
    }
    
    if (type === 'scroll' || type === 'all') {
      if (this.scrollData.length > 0) {
        dataToSend.scrolls = [...this.scrollData];
        this.scrollData = [];
      }
    }
    
    if (Object.keys(dataToSend).length === 0) return;
    
    this.trackEvent('heatmap_data', {
      page: window.location.pathname,
      viewportWidth: this.viewportWidth,
      viewportHeight: this.viewportHeight,
      ...dataToSend
    });
  }

  // Add this method to track heat map data
  trackHeatMap(data) {
    return this.trackEvent('heatmap_data', data);
  }

  // Add this method to track SPA route changes
  trackAllPageNavigations() {
    // Track initial page
    this.currentPage = window.location.pathname;
    
    // Use history API to track route changes
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      this.handleRouteChange();
    };
    
    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      this.handleRouteChange();
    };
    
    window.addEventListener('popstate', () => {
      this.handleRouteChange();
    });
  }

  // Handle route changes
  handleRouteChange() {
    const newPage = window.location.pathname;
    
    // If page changed, send any pending data for the previous page
    if (this.currentPage !== newPage) {
      this.sendHeatMapData('all');
      
      // Reset data collections for the new page
      this.clickData = [];
      this.moveData = [];
      this.scrollData = [];
      
      // Update current page
      this.currentPage = newPage;
      
      // Track page type for better segmentation
      const pageType = this.determinePageType(newPage);
      
      // Track page navigation
      this.trackEvent('page_navigation', {
        from: this.currentPage,
        to: newPage,
        pageType
      });
    }
  }

  // Determine page type for better segmentation
  determinePageType(path) {
    if (path.includes('/admin')) return 'admin';
    if (path.includes('/portal')) return 'portal';
    if (path.includes('/academy')) return 'academy';
    if (path.includes('/apply')) return 'application';
    if (path.includes('/donate')) return 'donation';
    return 'public';
  }

  // Track dynamic content interactions
  trackDynamicContentInteractions() {
    // Use mutation observer to track dynamically loaded content
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // New content was added to the page
          this.trackEvent('content_update', {
            page: window.location.pathname,
            targetId: mutation.target.id || null,
            targetClass: mutation.target.className || null,
            addedNodes: mutation.addedNodes.length
          });
        }
      });
    });
    
    // Start observing the document body
    observer.observe(document.body, { 
      childList: true, 
      subtree: true 
    });
  }

  // Track portal-specific interactions
  trackPortalInteractions() {
    // Check if we're in the portal
    if (!window.location.pathname.includes('/portal')) return;
    
    // Track document views
    document.addEventListener('click', (e) => {
      // Find closest document link or card
      const docElement = e.target.closest('[data-document-id]');
      if (docElement) {
        const documentId = docElement.dataset.documentId;
        const documentType = docElement.dataset.documentType;
        
        this.trackEvent('portal_document_interaction', {
          documentId,
          documentType,
          action: 'view',
          page: window.location.pathname
        });
      }
    });
  }

  // Track admin dashboard interactions
  trackAdminInteractions() {
    // Check if we're in the admin area
    if (!window.location.pathname.includes('/admin')) return;
    
    // Track admin-specific actions
    document.addEventListener('click', (e) => {
      // Track filter usage
      const filterElement = e.target.closest('[data-filter]');
      if (filterElement) {
        this.trackEvent('admin_filter_use', {
          filter: filterElement.dataset.filter,
          value: filterElement.value || filterElement.textContent,
          page: window.location.pathname
        });
      }
      
      // Track data exports
      const exportElement = e.target.closest('[data-export]');
      if (exportElement) {
        this.trackEvent('admin_data_export', {
          exportType: exportElement.dataset.export,
          page: window.location.pathname
        });
      }
    });
  }

  static disableForDevelopment() {
    if (process.env.NODE_ENV === 'development') {
      console.log('[TrackingService] Analytics disabled for development');
      this.isEnabled = false;
    }
  }

  static enableForDevelopment() {
    if (process.env.NODE_ENV === 'development') {
      console.log('[TrackingService] Analytics enabled for development');
      this.isEnabled = true;
    }
  }

  async getVisitorAnalytics() {
    try {
      // First try to get data from the API
      const token = localStorage.getItem('token');
      const response = await fetch(`${this.baseUrl}/api/analytics/visitors`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || ''}`,
          'Accept': 'application/json',
          'Origin': window.location.origin
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`API responded with status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.warn('Failed to fetch visitor analytics from API:', error);
      
      // If API fails, use locally collected data
      return this.getLocalVisitorData();
    }
  }

  getLocalVisitorData() {
    // Use data collected from the current session and stored events
    const sessionData = this.getSessionData();
    const storedEvents = this.getStoredEvents();
    
    // Calculate visitor trends from stored page view events
    const visitorTrends = this.calculateVisitorTrends(storedEvents);
    
    // Extract geographic data from stored events
    const geographicData = this.extractGeographicData(storedEvents);
    
    // Calculate new vs returning visitors
    const newVsReturning = this.calculateNewVsReturning(storedEvents);
    
    return {
      visitorTrends,
      geographicData,
      newVsReturning,
      totalVisitors: sessionData.uniqueVisitors || 0,
      averageTimeOnSite: this.formatTimeOnSite(sessionData.averageTimeOnSite || 0),
      bounceRate: `${sessionData.bounceRate || 0}%`,
      visitorChange: sessionData.visitorChange || 0,
      timeChange: sessionData.timeChange || 0,
      bounceChange: sessionData.bounceChange || 0
    };
  }

  calculateVisitorTrends(events) {
    // Group page view events by day
    const trendsByDay = {};
    
    events.forEach(event => {
      if (event.type === 'page_view') {
        const date = new Date(event.timestamp).toISOString().split('T')[0];
        trendsByDay[date] = (trendsByDay[date] || 0) + 1;
      }
    });
    
    // Convert to array format needed by charts
    return Object.entries(trendsByDay).map(([date, visitors]) => ({
      date,
      visitors
    }));
  }

  extractGeographicData(events) {
    // Count visitors by region
    const regionCounts = {};
    
    events.forEach(event => {
      if (event.location && event.location.region) {
        const region = event.location.region;
        regionCounts[region] = (regionCounts[region] || 0) + 1;
      }
    });
    
    // Convert to array format
    return Object.entries(regionCounts).map(([region, visitors]) => ({
      region,
      visitors
    }));
  }

  calculateNewVsReturning(events) {
    // Count unique visitors and determine if they're new or returning
    const visitors = {};
    
    events.forEach(event => {
      if (event.visitorId) {
        if (!visitors[event.visitorId]) {
          visitors[event.visitorId] = {
            firstSeen: event.timestamp,
            isReturning: false
          };
        } else if (!visitors[event.visitorId].isReturning) {
          // Check if this visit is on a different day
          const firstDay = new Date(visitors[event.visitorId].firstSeen).toDateString();
          const currentDay = new Date(event.timestamp).toDateString();
          
          if (firstDay !== currentDay) {
            visitors[event.visitorId].isReturning = true;
          }
        }
      }
    });
    
    // Count new vs returning
    const counts = Object.values(visitors).reduce(
      (acc, visitor) => {
        if (visitor.isReturning) {
          acc.returning++;
        } else {
          acc.new++;
        }
        return acc;
      },
      { new: 0, returning: 0 }
    );
    
    return counts;
  }

  formatTimeOnSite(seconds) {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  }
}

export default new TrackingService();